import type {JobHolder} from "../jobs/JobType.ts";
import type {MetlJobsTree} from "../debug/MetlJobsTree.ts";

/**
 * Generates unique ids, avoiding collisions with existing job ids.
 */
function idCounter(jobs: JobHolder[]): ()=>number {
    const existingIds = new Set(jobs.map(job => job.job.id));
    let currentId = 1;
    return function getNextId() {
        while (existingIds.has(currentId)) {
            currentId++;
        }
        const nextId = currentId;
        currentId++;
        return nextId;
    }
}

function pathMatch(pathA: string[], pathB: string[]): boolean {
    if (pathA.length !== pathB.length) {
        return false;
    }
    return pathA.every((segment, index) => segment === pathB[index]);
}

function identifySubPaths(currentPath: string[], jobs: JobHolder[]): string[] {
    const subPaths = new Set<string>();
    for (const job of jobs) {
        const jobPath = job.path || [];
        if (jobPath.length > currentPath.length && pathMatch(jobPath.slice(0, currentPath.length), currentPath)) {
            subPaths.add(jobPath[currentPath.length]);
        }
    }
    return Array.from(subPaths);
}

function inSubpath(path: string[], pathToCheck: string[]): boolean {
    if (pathToCheck.length < path.length) {
        return false;
    }
    return pathMatch(pathToCheck.slice(0, path.length), path);
}


function buildJobsTreeRecursive(jobsTree: MetlJobsTree, jobs: JobHolder[], pathSoFar: string[], counter: ()=>number) {
    const jobsAtThisLevel = jobs.filter(job => {
        const jobPath = job.path || [];
        return pathMatch(jobPath, pathSoFar);
    })
    for (const jobHolder of jobsAtThisLevel) {
        jobsTree.jobs.push({
            id: jobHolder.job.id,
            name: jobHolder.name,
            description: "",
            tag: undefined,
            type: jobHolder.type
        })
    }
    const subPaths = identifySubPaths(pathSoFar, jobs);
    for (const subPathSegment of subPaths) {
        const newPath = [...pathSoFar, subPathSegment];
        const childTree: MetlJobsTree = {
            id: counter(),
            name: subPathSegment,
            children: [],
            jobs: []
        };
        buildJobsTreeRecursive(childTree, jobs.filter(job => inSubpath(newPath, job.path || [])), newPath, counter);
        jobsTree.children.push(childTree);
    }

}

export function buildJobsTree(jobs: JobHolder[]): MetlJobsTree {
    const idCounterToUse = idCounter(jobs);
    const root: MetlJobsTree = {
        id: idCounterToUse(),
        jobs: [],
        children: [],
        name: "ROOT"
    }
    buildJobsTreeRecursive(root, jobs, ["ROOT"], idCounterToUse)
    return root;
}